<script setup lang="ts">
defineProps<{
  project: {
    slug: string
    title: string
    type: string
    image: string
    verticalImage?: string
    imageAlt?: string
    formation: string
    promotionName?: string
    tags: string[]
    duration?: string
    teamCount?: number
    realizationDate?: string | null
  }
}>()

const getTypeColor = (type: string) => {
  const t = type?.toLowerCase() || ''
  
  // Jeu de Plateau (Warm/Wood)
  if (t.includes('plateau')) return 'from-amber-600 to-orange-600'
  
  // Jeu Vidéo (Core Purple)
  if (t.includes('jeu vid')) return 'from-purple-600 to-indigo-600'
  
  // Dev & Tech (Blue/Cyan)
  if (['dev', 'tech', 'qa', 'test'].some(k => t.includes(k))) 
    return 'from-cyan-500 to-blue-600'
    
  // Art & Visuals (Pink/Rose)
  if (['art', 'vfx'].some(k => t.includes(k))) 
    return 'from-pink-500 to-rose-500'

  // Motion & Animation (Dynamic Orange)
  if (['motion', 'animation'].some(k => t.includes(k))) 
    return 'from-orange-500 to-yellow-500'

  // UI/UX (Digital Purple)
  if (['ui', 'ux'].some(k => t.includes(k))) 
    return 'from-fuchsia-500 to-purple-500'
    
  // Design (Red/Orange)
  if (['design'].some(k => t.includes(k))) 
    return 'from-oxo-red to-orange-600'
    
  // Audio (Green/Emerald)
  if (['musique', 'sfx', 'son'].some(k => t.includes(k))) 
    return 'from-teal-400 to-emerald-500'
    
  // Produce/Marketing (Corporate/Serious)
  if (['producing', 'marketing'].some(k => t.includes(k))) 
    return 'from-slate-500 to-slate-700'
    
  // Default (Red)
  return 'from-oxo-red to-rose-600'
}
</script>

<template>
  <article class="group relative bg-oxo-card-bg/30 rounded-2xl overflow-hidden border border-white/5 hover:border-white/20 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(230,0,0,0.2)] hover:-translate-y-2 w-full flex flex-col">
    <NuxtLink :to="`/projets/${project.slug}`" class="absolute inset-0 z-20" :aria-label="`Découvrir ${project.title}`"></NuxtLink>
    
    <!-- Image Container (Steam Vertical Capsule Ratio: 2/3) -->
    <div class="relative aspect-2/3 overflow-hidden min-h-0 flex-none">
      <NuxtImg loading="lazy"
        :src="project.verticalImage || project.image"
        :alt="project.imageAlt || project.title"
        class="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
        sizes="50vw md:33vw lg:25vw"
      />
      
      <!-- Gradient Overlay -->
      <div class="absolute inset-0 bg-linear-to-t from-background via-background/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
      
      <!-- Type Badge -->
      <div v-if="project.type && project.type !== 'None'" class="absolute top-2 left-2 md:top-4 md:left-4 z-10">
        <UiBadge variant="warning" size="xs">
          {{ project.type }}
        </UiBadge>
      </div>
      
      <!-- Hover Effect: Discover Button -->
      <!-- Show on Hover (Mouse) OR on devices with NO hover capability (Touch/Tablet/Mobile) -->
      <!-- [@media(hover:none)]:opacity-100 ensures it's visible on iPads/Tablets/Mobiles regardless of screen size -->
      <div class="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-all duration-500 scale-90 group-hover:scale-100 [@media(hover:none)]:scale-100 group-active:scale-100">
        <AppButton variant="secondary" size="sm" class="shadow-[0_0_40px_rgba(247,196,50,0.4)] transform -translate-y-4 group-hover:translate-y-0 [@media(hover:none)]:translate-y-0 transition-all duration-500 ease-out pointer-events-none">
          Découvrir
          <i class="fas fa-arrow-right ml-2" aria-hidden="true"></i>
        </AppButton>
      </div>
    </div>
    
    <!-- Content (Fixed Height to ensure alignment) -->
    <div class="p-2 md:p-3 flex-none h-[90px] bg-oxo-card-bg/80 backdrop-blur-xs z-30 relative">
      <!-- Title -->
      <h3 class="font-display font-bold md:font-black text-[10px] md:text-sm text-foreground mb-1 line-clamp-2 dark:group-hover:text-oxo-yellow transition-colors duration-300 uppercase leading-tight">
        {{ project.title }}
      </h3>
      
      <!-- Subtitle: Formation & Promotion -->
      <p class="text-[10px] text-foreground-dim mb-2">
        <span v-if="project.formation">{{ project.formation }}</span>
        <span v-if="project.formation && project.promotionName"> · </span>
        <span v-if="project.promotionName">{{ project.promotionName }}</span>
      </p>
      
      <!-- Meta info row -->
      <div class="flex items-center gap-3 text-[10px] text-foreground-dim/70">
        <span v-if="project.teamCount" class="flex items-center gap-1">
          <i class="fas fa-users text-[8px]" aria-hidden="true"></i>
          {{ project.teamCount }}
        </span>
        <span v-if="project.duration" class="flex items-center gap-1">
          <i class="fas fa-clock text-[8px]" aria-hidden="true"></i>
          {{ project.duration }}
        </span>
      </div>
      
      <!-- Tags row -->
      <div v-if="project.tags && project.tags.length > 0" class="flex flex-wrap items-center gap-1.5 mt-3">
        <span 
          v-for="tag in project.tags.slice(0, 3)" 
          :key="tag"
          class="px-2 py-0.5 text-[10px] font-medium bg-white/5 text-foreground-dim/80 rounded-sm border border-white/5"
        >
          {{ tag }}
        </span>
        <span 
          v-if="project.tags.length > 3"
          class="px-2 py-0.5 text-[10px] font-medium text-oxo-red/80"
        >
          +{{ project.tags.length - 3 }}
        </span>
      </div>
    </div>
  </article>
</template>

<style scoped>
/* Subtle glow effect on hover */
.group:hover {
  box-shadow: 
    0 20px 60px rgba(230, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}
</style>
